// FIRST IMPORT DECLARATION — DO NOT MOVE.
// Side-effect import: constructs the shared Supabase client and installs the
// one-shot PASSWORD_RECOVERY capture in the same synchronous tick, before App
// and before any other module whose dependency graph can reach the shared
// client is evaluated. ES modules evaluate dependencies in source order, so
// this guarantee comes from THIS declaration being first — not from any
// assumption about supabase-js deferring initialization (BF-A / BF-V4-1).
import "./integrations/supabase/recoveryHandoff";
// Google OAuth callback params are scrubbed inside recoveryHandoff, before the
// shared client is constructed (see that module's first import).
import { supabase } from "./integrations/supabase/client";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { startVersionSync, checkVersionNow } from "./lib/versionSync";
import { installDiagnosticsBuffer } from "./utils/diagnosticsBuffer";


installDiagnosticsBuffer();

// "ResizeObserver loop ..." is a benign browser notice (observers deliver the
// remaining notifications next frame). Stop it from reaching error overlays
// that would otherwise treat it as a fatal crash. Only this exact message.
window.addEventListener(
  "error",
  (e) => {
    if (typeof e.message === "string" && e.message.startsWith("ResizeObserver loop")) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

const PWA_RESET_VERSION = "2026-04-19-preview-sw-disable";
const RESET_MARKER_KEY = `workitect-pwa-reset-${PWA_RESET_VERSION}`;
const WRIKE_OAUTH_STORAGE_KEY = "wrike.oauthPayload";

function isOneDrivePickerPopup() {
  return Boolean(
    window.opener &&
    window.location.pathname === "/app"
  );
}

function bootstrapOneDrivePickerPopup() {
  document.documentElement.style.background = "white";
  document.body.innerHTML = "";
  const script = document.createElement("script");
  script.src = "https://js.live.net/v7.2/OneDrive.js";
  script.async = true;
  script.onerror = () => window.close();
  document.head.appendChild(script);
}

function relayWrikeOAuthFromHash() {
  if (!window.location.hash.startsWith("#wrikeOAuth=")) return;
  const encoded = window.location.hash.slice("#wrikeOAuth=".length);
  try {
    const payload = decodeURIComponent(encoded);
    window.opener?.postMessage(payload, window.location.origin);
    window.localStorage.setItem(WRIKE_OAUTH_STORAGE_KEY, payload);
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    setTimeout(() => { try { window.close(); } catch { /* noop */ } }, 250);
  } catch {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }
}

function shouldEnableServiceWorker() {
  const { hostname } = window.location;

  return !(
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("lovableproject.com") ||
    hostname.startsWith("id-preview--")
  );
}

async function resetStalePwaCachesIfNeeded(): Promise<boolean> {
  try {
    if (window.localStorage.getItem(RESET_MARKER_KEY) === "done") return false;

    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((k) => window.caches.delete(k)));
    }

    window.localStorage.setItem(RESET_MARKER_KEY, "done");
    return true;
  } catch {
    return false;
  }
}

async function bootstrap() {
  if (isOneDrivePickerPopup()) {
    bootstrapOneDrivePickerPopup();
    return;
  }

  relayWrikeOAuthFromHash();

  const enableServiceWorker = shouldEnableServiceWorker();

  const hideSplash = () => {
    const splash = document.getElementById("pwa-splash");
    if (!splash) return;
    splash.style.opacity = "0";
    setTimeout(() => splash.remove(), 300);
  };

  let hidden = false;
  const hideOnce = () => { if (hidden) return; hidden = true; hideSplash(); };

  // For non-/app routes (landing, marketing, public pages) the splash adds
  // no value — hide it as soon as the JS bundle starts executing so users
  // see content immediately as React mounts.
  const isAppRoute = window.location.pathname.startsWith("/app");
  if (!isAppRoute) {
    requestAnimationFrame(hideOnce);
  }

  createRoot(document.getElementById("root")!).render(
    <HelmetProvider>
      <App />
    </HelmetProvider>
  );

  window.setTimeout(() => void resetStalePwaCachesIfNeeded(), 2_000);

  // Background version sync — detects new deployments and hard-reloads
  // exactly once when a mismatch is found. Safe in iframes/preview.
  startVersionSync();

  // Force a fresh check whenever the auth state changes (login/logout/refresh)
  // so users land on the latest build immediately after signing in.
  // Never purge caches or hard-reload during login; it makes the first app
  // paint feel broken. Version sync continues in the background.
  supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_IN") {
      window.setTimeout(() => void checkVersionNow({ reloadIfStale: false }), 2_000);
    } else if (event === "TOKEN_REFRESHED") {
      void checkVersionNow({ reloadIfStale: false });
    }
  });

  // The in-app shell owns loading states. Keep only a tiny cap for catastrophic
  // JS failures so users never stare at the logo.
  window.addEventListener("workitect-app-ready", hideOnce, { once: true });
  setTimeout(hideOnce, 250);

  if (enableServiceWorker && "serviceWorker" in navigator) {
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "SW_UPDATED" && !reloading) {
        reloading = true;
        window.location.reload();
      }
    });

    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js", { updateViaCache: "none" })
        .then((registration) => {
          registration.update();
          // Poll for updates every 60s so long-lived tabs pick up new builds.
          setInterval(() => registration.update().catch(() => {}), 60_000);
          registration.addEventListener("updatefound", () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
              if (installing.state === "installed" && navigator.serviceWorker.controller) {
                // New SW ready — activate immediately.
                installing.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });
        })
        .catch(() => {});
    });
  }
}

bootstrap();
